import { useState } from 'react'
import type { PowerPerformanceForms } from '../../../../types/mutual-fund-detail.types'
import {
  FundSummaryMetricKey,
  FUND_SUMMARY_METRIC_LABELS,
  type FundSummaryMetrics
} from '../../types/recommendation.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { useScaleAnimation } from '../../../../hooks/useScaleAnimation'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { FundCardLayout, FundCardBorderVariant } from './FundCardLayout'
import { StyledAmount } from './StyledAmount'

const BLACK = 'text-[#232323] text-[13px] font-[500]'
const GREY = 'text-[#6A6E6E] text-[13px] font-[400]'

const METRIC_ORDER: FundSummaryMetricKey[] = [
  FundSummaryMetricKey.INVESTMENT,
  FundSummaryMetricKey.XIRR,
  FundSummaryMetricKey.ONE_DAY_RETURNS,
  FundSummaryMetricKey.RETURNS
]

function formatPercent(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value >= 0 ? '+' : '-'} ${Math.abs(value).toFixed(2)}%`
}

type FundSummaryCardProps = {
  fundLogo: string
  fundTitle: string
  fundNature: string
  fundCategory: string
  powerRank: number
  powerRating?: `${PowerPerformanceForms}`
  isRegular: boolean
  metrics?: FundSummaryMetrics | null
  /** Legacy: when metrics is absent, this is shown as current value for Investment */
  amount?: number
}

export function FundSummaryCard({
  fundLogo,
  fundTitle,
  fundNature,
  fundCategory,
  powerRank,
  powerRating,
  isRegular,
  metrics,
  amount
}: FundSummaryCardProps) {
  const hasInvestment =
    (metrics?.currentValue != null && Number.isFinite(metrics.currentValue)) ||
    (metrics?.initialInvestment != null && Number.isFinite(metrics.initialInvestment)) ||
    (amount != null && Number.isFinite(amount))
  const effectiveCurrentValue = metrics?.currentValue ?? amount
  const effectiveInitialInvestment = metrics?.initialInvestment

  const availableMetrics = METRIC_ORDER.filter((key) => {
    if (key === FundSummaryMetricKey.INVESTMENT) return hasInvestment
    if (key === FundSummaryMetricKey.XIRR) return metrics?.xirr != null && Number.isFinite(metrics.xirr)
    if (key === FundSummaryMetricKey.ONE_DAY_RETURNS)
      return (
        (metrics?.oneDayReturns != null && Number.isFinite(metrics.oneDayReturns)) ||
        (metrics?.oneDayReturnsValue != null && Number.isFinite(metrics.oneDayReturnsValue))
      )
    if (key === FundSummaryMetricKey.RETURNS) return metrics?.returns != null && Number.isFinite(metrics.returns)
    return false
  })

  const defaultMetric: FundSummaryMetricKey =
    availableMetrics.length > 0 ? availableMetrics[0] : FundSummaryMetricKey.INVESTMENT
  const [selectedMetric, setSelectedMetric] = useState<FundSummaryMetricKey>(defaultMetric)
  const { scaleStyle, triggerAnimation } = useScaleAnimation()

  const cycleMetric = () => {
    if (availableMetrics.length <= 1) return
    triggerAnimation()
    const idx = availableMetrics.indexOf(selectedMetric)
    const next = (idx + 1) % availableMetrics.length
    setSelectedMetric(availableMetrics[next] ?? defaultMetric)
  }

  const hasToggle = availableMetrics.length > 1
  const displayLabel = FUND_SUMMARY_METRIC_LABELS[selectedMetric]

  const trailingContent = (() => {
    if (selectedMetric === FundSummaryMetricKey.INVESTMENT) {
      const hasCurrent = effectiveCurrentValue != null && Number.isFinite(effectiveCurrentValue)
      const hasInitial = effectiveInitialInvestment != null && Number.isFinite(effectiveInitialInvestment)
      if (!hasCurrent && !hasInitial) return <span className={GREY}>—</span>
      return (
        <div className="flex flex-col items-end gap-0.5">
          {hasInitial && (
            <span className={BLACK}>
              {roundOffAndFormatNumberWithCurrency(effectiveCurrentValue ?? 0)}
            </span>
          )}
          {hasCurrent && (
            <span className={GREY}>
              {roundOffAndFormatNumberWithCurrency(effectiveInitialInvestment ?? 0)}
            </span>
          )}
        </div>
      )
    }
    if (selectedMetric === FundSummaryMetricKey.XIRR) {
      return (
        <span className={BLACK}>
          {formatPercent(metrics?.xirr)}
        </span>
      )
    }
    if (selectedMetric === FundSummaryMetricKey.ONE_DAY_RETURNS) {
      const value = metrics?.oneDayReturnsValue
      const percentage = metrics?.oneDayReturns
      const hasValue = value != null && Number.isFinite(value)
      const hasPercentage = percentage != null && Number.isFinite(percentage)
      if (!hasValue && !hasPercentage) return <span className={GREY}>—</span>
      return (
        <div className="flex flex-col items-end gap-0.5">
          {hasValue && (
            <StyledAmount amount={value} colorVariant="light" showSign className="text-right" />
          )}
          {hasPercentage && (
            <span className={GREY}>
              {formatPercent(percentage)}
            </span>
          )}
        </div>
      )
    }
    if (selectedMetric === FundSummaryMetricKey.RETURNS) {
      const value = metrics?.returnsValue
      const percentage = metrics?.returns
      const hasValue = value != null && Number.isFinite(value)
      const hasPercentage = percentage != null && Number.isFinite(percentage)
      if (!hasValue && !hasPercentage) return <span className={GREY}>—</span>
      return (
        <div className="flex flex-col items-end gap-0.5">
          {hasValue && (
            <StyledAmount amount={value} colorVariant="light" showSign className="text-right" />
          )}
          {hasPercentage && (
            <span className={GREY}>
              {formatPercent(percentage)}
            </span>
          )}
        </div>
      )
    }
    return null
  })()

  return (
    <div className="flex flex-col w-full bg-white border border-[#F0F4F7] rounded-xl overflow-visible">
      <div className="flex justify-between items-center px-4 pt-4 pb-3 border-b border-[#F0F4F7]">
        <span className="text-[13px] font-[400] text-[#6A6E6E]">Fund</span>
        {hasToggle ? (
          <div
            onClick={(e) => {
              e.stopPropagation()
              cycleMetric()
            }}
            className="flex items-center gap-1 text-[13px] font-[400] text-[#6A6E6E]"
            style={scaleStyle}
          >
            {displayLabel}
            <RenderHTML innerHTMLText={TOGGLE_ICON} />
          </div>
        ) : (
          <span className="text-[13px] font-[500] text-[#6A6E6E]">{displayLabel}</span>
        )}
      </div>
      <div className="px-0">
        <FundCardLayout
          fundLogo={fundLogo}
          fundTitle={fundTitle}
          fundNature={fundNature}
          fundCategory={fundCategory}
          trailingContent={trailingContent}
          trailingContentAlign="start"
          powerRank={powerRank}
          powerRating={powerRating}
          isRegular={isRegular}
          borderVariant={FundCardBorderVariant.NO_BORDER}
          topPadding={16}
          rightPadding={16}
          bottomPadding={15}
          leftPadding={16}
        />
      </div>
    </div>
  )
}

export const TOGGLE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="5" height="12" viewBox="0 0 5 12" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M2.85355 1.66476C2.65829 1.44508 2.34171 1.44508 2.14645 1.66476L0.146463 3.91476C0.00346387 4.07563 -0.0393159 4.31756 0.0380737 4.52776C0.115463 4.73795 0.297782 4.875 0.500011 4.875H4.49999C4.70222 4.875 4.88454 4.73795 4.96193 4.52776C5.03932 4.31756 4.99654 4.07563 4.85354 3.91476L2.85355 1.66476ZM0.0380737 7.47224C0.115463 7.26205 0.297782 7.125 0.500011 7.125H4.49999C4.70222 7.125 4.88454 7.26205 4.96193 7.47224C5.03932 7.68243 4.99654 7.92437 4.85354 8.08524L2.85355 10.3353C2.65829 10.5549 2.34171 10.5549 2.14645 10.3353L0.146463 8.08524C0.00346387 7.92437 -0.0393159 7.68243 0.0380737 7.47224Z" fill="#6A6E6E"/>
</svg>`
